<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import type {
    UsageProfileOpeningHours,
    UsageProfileWeekDay,
} from "@modules/usage-profile/types"

// ================================
// CONSTANTS
// ================================
const WEEK_DAYS = [
    "lundi",
    "mardi",
    "mercredi",
    "jeudi",
    "vendredi",
    "samedi",
    "dimanche",
] as const

const MAX_TIME_SLOTS = 3

type DayMode = "ouvert" | "ferme" | "custom"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            heading1: "Horaires de la semaine",
            useHours: "Appliquer les horaires de {day} à",
            open: "Ouvert H24",
            closed: "Fermé H24",
            custom: "Personnalisé",
            explain:
                "Veuillez renseigner au moins une plage horaire ou cocher ouvert/fermé pour chaque jour.",
            opening: "Ouverture",
            closing: "Fermeture",
            addTimeSlot: "Ajouter une plage horaire",
            validate: "Valider",
            cancel: "Annuler",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** Liste des jours avec horaires */
    value: UsageProfileWeekDay[]
}>()

const emit = defineEmits<{
    input: [
        payload: {
            week: UsageProfileWeekDay[]
            disabled: boolean
        },
    ]
}>()

// ================================
// DONNÉES CALCULÉES
// ================================
/** Jours de la semaine avec valeurs par défaut */
const getWeek = computed(() => {
    return WEEK_DAYS.map((jour) => {
        const existing = props.value?.find(
            (d) => d.jour.toLowerCase() === jour.toLowerCase()
        )
        return {
            jour,
            ouvert: existing?.ouvert ?? false,
            ferme: existing?.ferme ?? false,
            horaires_ouverture:
                existing?.horaires_ouverture?.length &&
                existing.horaires_ouverture.every((h) => h.debut && h.fin)
                    ? existing.horaires_ouverture.map((h) => ({ ...h }))
                    : existing?.horaires_ouverture?.length
                      ? existing.horaires_ouverture.map((h) => ({
                            debut: h.debut || "08:00",
                            fin: h.fin || "12:00",
                        }))
                      : [],
        } as UsageProfileWeekDay
    })
})

/**
 * Retourne le mode d’un jour (ouvert H24, fermé H24, ou personnalisé).
 * @param day - Jour du profil
 * @returns Mode du jour
 */
function getDayMode(day: UsageProfileWeekDay): DayMode {
    if (day.ouvert) return "ouvert"
    if (day.ferme) return "ferme"
    if (day.horaires_ouverture?.length) return "custom"
    return "ferme"
}

/**
 * Légende affichée pour un jour en mode vue (Ouvert H24, Fermé H24, ou plages horaires).
 * @param day - Jour du profil
 * @returns Chaîne à afficher
 */
function getDayLegend(day: UsageProfileWeekDay): string {
    const mode = getDayMode(day)
    if (mode === "ouvert") return t("open")
    if (mode === "ferme") return t("closed")
    if (mode === "custom" && day.horaires_ouverture?.length) {
        return day.horaires_ouverture
            .map((h) => `${h.debut} - ${h.fin}`)
            .join(", ")
    }
    return "-"
}

/**
 * Liste des autres jours (exclut le jour courant) pour appliquer la config.
 * @param currentJour - Jour courant
 * @returns Jours restants
 */
function getOtherDays(currentJour: string): readonly string[] {
    return WEEK_DAYS.filter(
        (d) => d.toLowerCase() !== currentJour.toLowerCase()
    )
}

/**
 * Vérifie si au moins un jour n’a ni ouvert, ni fermé, ni horaires.
 * @param days - Semaine
 * @returns true si au moins un jour est vide
 */
function isSomeDaysEmpty(days: UsageProfileWeekDay[]): boolean {
    return days.some(
        (day) =>
            !day.ouvert &&
            !day.ferme &&
            (!day.horaires_ouverture || day.horaires_ouverture.length === 0)
    )
}

const isSomeSlotsEmpty = computed(() => isSomeDaysEmpty(getWeek.value))

// ================================
// STATE (édition d’un jour)
// ================================
/** Jour actuellement en cours d'édition (null = aucun) */
const editingDay = ref<string | null>(null)

/** Mode sélectionné dans le formulaire d'édition (local, non appliqué tant qu'on ne valide pas) */
const editPickedMode = ref<DayMode>("ferme")

/** Horaires dans le formulaire d'édition (local) */
const editHoraires = ref<UsageProfileOpeningHours[]>([])

/** Jours cochés pour appliquer la config (local) */
const editApplyToDays = ref<Record<string, boolean>>({})

/** Ouvre le formulaire d’édition pour le jour donné */
function onOpenEditForm(jour: string): void {
    const day = getWeek.value.find(
        (d) => d.jour.toLowerCase() === jour.toLowerCase()
    )
    if (!day) return

    editingDay.value = jour
    editPickedMode.value = getDayMode(day)
    editHoraires.value =
        day.horaires_ouverture?.length &&
        day.horaires_ouverture.every((h) => h.debut && h.fin)
            ? day.horaires_ouverture.map((h) => ({ ...h }))
            : [
                  { debut: "08:00", fin: "12:00" },
                  { debut: "14:00", fin: "18:00" },
              ]

    const initial: Record<string, boolean> = {}
    getOtherDays(jour).forEach((d) => {
        initial[d] = false
    })
    editApplyToDays.value = { ...initial }
}

/** Ferme le formulaire d’édition sans appliquer */
function onCancelEdit(): void {
    editingDay.value = null
}

/** Applique les modifications et ferme le formulaire */
function onValidateEdit(): void {
    const jour = editingDay.value
    if (!jour) return

    const weekCopy = getWeek.value.map((d) => ({ ...d }))
    const dayToModify = weekCopy.find(
        (d) => d.jour.toLowerCase() === jour.toLowerCase()
    )
    if (!dayToModify) return

    const sourceData: Partial<UsageProfileWeekDay> = {
        ouvert: editPickedMode.value === "ouvert",
        ferme: editPickedMode.value === "ferme",
        horaires_ouverture:
            editPickedMode.value === "custom"
                ? editHoraires.value.filter((s) => s.debut && s.fin)
                : [],
    }

    Object.assign(dayToModify, sourceData)

    const targetJours = Object.entries(editApplyToDays.value)
        .filter(([, checked]) => checked)
        .map(([d]) => d)

    for (const targetJour of targetJours) {
        const dayToApply = weekCopy.find(
            (d) => d.jour.toLowerCase() === targetJour.toLowerCase()
        )
        if (dayToApply) {
            dayToApply.ouvert = sourceData.ouvert ?? false
            dayToApply.ferme = sourceData.ferme ?? false
            dayToApply.horaires_ouverture = (
                sourceData.horaires_ouverture ?? []
            ).map((h) => ({ ...h }))
        }
    }

    emit("input", {
        week: weekCopy,
        disabled: isSomeDaysEmpty(weekCopy),
    })

    editingDay.value = null
}

// ================================
// INTERACTIONS (mode Personnalisé)
// ================================
function onEditTimeSlotChange(
    index: number,
    field: "debut" | "fin",
    value: string
) {
    const slots = [...editHoraires.value]
    if (index >= slots.length) return
    slots[index] = { ...slots[index], [field]: value }
    editHoraires.value = slots
}

function onAddTimeSlot() {
    if (editHoraires.value.length >= MAX_TIME_SLOTS) return
    const last = editHoraires.value[editHoraires.value.length - 1]
    editHoraires.value = [
        ...editHoraires.value,
        last
            ? { debut: last.fin, fin: "18:00" }
            : { debut: "08:00", fin: "12:00" },
    ]
}

function onRemoveTimeSlot(index: number) {
    if (editHoraires.value.length <= 1) return
    editHoraires.value = editHoraires.value.filter((_, i) => i !== index)
}

function onToggleApplyToDay(otherDay: string) {
    editApplyToDays.value = {
        ...editApplyToDays.value,
        [otherDay]: !editApplyToDays.value[otherDay],
    }
}

/** Désactive le bouton Valider si mode Personnalisé et horaires invalides */
const getIsValidateDisabled = computed(() => {
    if (editPickedMode.value !== "custom") return false
    const slots = editHoraires.value.filter((s) => s.debut && s.fin)
    return slots.length === 0
})
</script>

<template>
    <div class="usage-profile-edit-week flex flex-col gap-4">
        <h3 class="text-lg font-semibold text-primary-700">
            {{ t("heading1") }}
        </h3>
        <BaseAppMessage
            v-if="isSomeSlotsEmpty"
            severity="warn"
            class="mb-3"
            :message="t('explain')"
        />
        <div class="flex flex-col gap-2">
            <div
                v-for="day in getWeek"
                :key="day.jour"
                class="usage-profile-edit-week__row flex flex-col gap-0 p-0 rounded-lg"
            >
                <!-- Mode vue : jour + légende + bouton crayon -->
                <div
                    v-if="editingDay !== day.jour"
                    class="usage-profile-edit-week__view flex items-center justify-between gap-4 flex-wrap p-3 min-h-12"
                >
                    <span class="capitalize font-medium min-w-24">{{
                        day.jour
                    }}</span>
                    <div class="flex items-center gap-3 flex-wrap">
                        <span class="text-gray-600">
                            {{ getDayLegend(day) }}
                        </span>
                        <BaseButton
                            icon="pi pi-pencil"
                            severity="secondary"
                            text
                            rounded
                            size="small"
                            :aria-label="'Modifier'"
                            @click="onOpenEditForm(day.jour)"
                        />
                    </div>
                </div>

                <!-- Mode édition : formulaire expansible -->
                <div
                    v-else
                    class="usage-profile-edit-week__edit flex flex-col gap-4 p-4 rounded-lg bg-primary-10"
                >
                    <h4 class="font-medium capitalize text-primary-700">
                        {{ day.jour }}
                    </h4>

                    <!-- 3 radios : Ouvert H24 / Fermé H24 / Personnalisé -->
                    <div class="flex flex-col gap-2">
                        <div class="flex flex-wrap gap-4">
                            <div class="flex items-center gap-2">
                                <BaseRadioButton
                                    :input-id="`edit-open-${day.jour}`"
                                    :model-value="editPickedMode"
                                    value="ouvert"
                                    @update:model-value="
                                        editPickedMode = $event
                                    "
                                />
                                <label
                                    :for="`edit-open-${day.jour}`"
                                    class="cursor-pointer"
                                >
                                    {{ t("open") }}
                                </label>
                            </div>
                            <div class="flex items-center gap-2">
                                <BaseRadioButton
                                    :input-id="`edit-closed-${day.jour}`"
                                    :model-value="editPickedMode"
                                    value="ferme"
                                    @update:model-value="
                                        editPickedMode = $event
                                    "
                                />
                                <label
                                    :for="`edit-closed-${day.jour}`"
                                    class="cursor-pointer"
                                >
                                    {{ t("closed") }}
                                </label>
                            </div>
                            <div class="flex items-center gap-2">
                                <BaseRadioButton
                                    :input-id="`edit-custom-${day.jour}`"
                                    :model-value="editPickedMode"
                                    value="custom"
                                    @update:model-value="
                                        editPickedMode = $event
                                    "
                                />
                                <label
                                    :for="`edit-custom-${day.jour}`"
                                    class="cursor-pointer"
                                >
                                    {{ t("custom") }}
                                </label>
                            </div>
                        </div>

                        <!-- Sous-formulaire Personnalisé : plages horaires -->
                        <div
                            v-if="editPickedMode === 'custom'"
                            class="flex flex-col gap-3 pl-4 mt-2 border-l-1 border-gray-200"
                        >
                            <div
                                v-for="(slot, index) in editHoraires"
                                :key="index"
                                class="flex items-center gap-3 flex-wrap"
                            >
                                <BaseInputWrapper
                                    :input-id="`edit-debut-${day.jour}-${index}`"
                                    :label="t('opening')"
                                    unfloat
                                    orientation="horizontal"
                                >
                                    <BaseInputText
                                        :id="`edit-debut-${day.jour}-${index}`"
                                        :model-value="slot.debut"
                                        type="time"
                                        class="w-28"
                                        @update:model-value="
                                            onEditTimeSlotChange(
                                                index,
                                                'debut',
                                                $event ?? ''
                                            )
                                        "
                                    />
                                </BaseInputWrapper>
                                <BaseInputWrapper
                                    :input-id="`edit-fin-${day.jour}-${index}`"
                                    :label="t('closing')"
                                    unfloat
                                    orientation="horizontal"
                                >
                                    <BaseInputText
                                        :id="`edit-fin-${day.jour}-${index}`"
                                        :model-value="slot.fin"
                                        type="time"
                                        class="w-28"
                                        @update:model-value="
                                            onEditTimeSlotChange(
                                                index,
                                                'fin',
                                                $event ?? ''
                                            )
                                        "
                                    />
                                </BaseInputWrapper>
                                <BaseButton
                                    v-if="editHoraires.length > 1"
                                    icon="pi pi-trash"
                                    severity="secondary"
                                    text
                                    rounded
                                    size="small"
                                    :aria-label="'Supprimer la plage horaire'"
                                    @click="onRemoveTimeSlot(index)"
                                />
                            </div>
                            <BaseButton
                                v-if="editHoraires.length < MAX_TIME_SLOTS"
                                :label="t('addTimeSlot')"
                                icon="pi pi-plus"
                                size="small"
                                ghost
                                class="w-fit"
                                @click="onAddTimeSlot"
                            />
                        </div>
                    </div>

                    <!-- Appliquer à d'autres jours -->
                    <div class="flex flex-col gap-2">
                        <span class="font-medium text-primary-700">
                            {{
                                t("useHours", {
                                    day:
                                        day.jour.charAt(0).toUpperCase() +
                                        day.jour.slice(1),
                                })
                            }}
                        </span>
                        <div
                            class="flex flex-wrap gap-x-6 gap-y-2 items-center"
                        >
                            <div
                                v-for="otherDay in getOtherDays(day.jour)"
                                :key="otherDay"
                                class="flex items-center gap-2"
                            >
                                <BaseCheckbox
                                    :model-value="
                                        editApplyToDays[otherDay] ?? false
                                    "
                                    :input-id="`edit-dup-${day.jour}-${otherDay}`"
                                    :binary="true"
                                    @update:model-value="
                                        onToggleApplyToDay(otherDay)
                                    "
                                />
                                <label
                                    :for="`edit-dup-${day.jour}-${otherDay}`"
                                    class="cursor-pointer capitalize"
                                >
                                    {{ otherDay }}
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Boutons Valider / Annuler -->
                    <div class="flex gap-2 pt-2">
                        <BaseButton
                            :label="t('cancel')"
                            severity="secondary"
                            size="small"
                            ghost
                            @click="onCancelEdit"
                        />
                        <BaseButton
                            :label="t('validate')"
                            severity="primary"
                            size="small"
                            :disabled="getIsValidateDisabled"
                            @click="onValidateEdit"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
